<script>
import VueApexCharts from 'vue-apexcharts'

export default {
	name: 'GeneralTab',
	components: { VueApexCharts },
	props: {
		// common
		hasActiveFilters: { type: Boolean, default: false },
		activeFilters: { type: Array, default: () => [] },
		// users
		loadingUsers: { type: Boolean, default: false },
		usersSeries: { type: Array, default: () => [] },
		usersChartOptions: { type: Object, default: () => ({}) },
		totalUsers: { type: Number, default: 0 },
		// quotas
		loadingQuotas: { type: Boolean, default: false },
		quotasSeries: { type: Array, default: () => [] },
		quotasChartOptions: { type: Object, default: () => ({}) },
		totalQuotas: { type: Number, default: 0 },
		totalQuotasFishVolume: { type: Number, default: 0 },
		// fish (general)
		loadingFish: { type: Boolean, default: false },
		fishSeries: { type: Array, default: () => [] },
		fishChartOptions: { type: Object, default: () => ({}) },
		totalFishTrades: { type: Number, default: 0 },
		totalFishVolume: { type: Number, default: 0 },
		// reports
		loadingReports: { type: Boolean, default: false },
		reportsSeries: { type: Array, default: () => [] },
		reportsChartOptions: { type: Object, default: () => ({}) },
		reportsYear: { type: Number, default: null },
		reportsFilters: { type: Object, required: true },
		// bin mode sections used in general tab
		isBinMode: { type: Boolean, default: false },
		binSearched: { type: Boolean, default: false },
		loadingBin: { type: Boolean, default: false },
		binSeries: { type: Array, default: () => [] },
		binChartOptions: { type: Object, default: () => ({}) },
		binUser: { type: Object, default: null },
		binUserInfo: { type: Object, default: null },
		// handlers
		onSearchReports: { type: Function, required: true },
		onClearReportsFilters: { type: Function, required: true },
		// for filter chips clear button in users section
		onClearFilter: { type: Function, required: false },
		getFilterType: { type: Function, required: false },
	},
}
</script>

<template>
	<div>
		<!-- BIN Analytics Chart -->
		<div v-if="isBinMode && binSearched">
			<div class="mb-4">
				<h3 class="font-main font-semibold text-[18px] text-main mb-2">
					{{ $t('titles.bin_analytics') || 'Аналитика по БИН' }}
					<span v-if="binUserInfo" class="text-[14px] font-normal text-third ml-2">
						({{ binUserInfo.iin_bin }})
					</span>
				</h3>
				<!-- Active Filters Display -->
				<div v-if="hasActiveFilters" class="mb-3">
					<div class="flex flex-wrap gap-2">
						<div
							v-for="(filter, index) in activeFilters"
							:key="index"
							class="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-sm text-blue-700 flex items-center gap-2"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
									clip-rule="evenodd"
								></path>
							</svg>
							<span>{{ filter }}</span>
						</div>
					</div>
				</div>
			</div>
			<div v-if="loadingBin" class="flex items-center justify-center h-[300px]">
				<v-preloader :message="{ status: null, text: null }"></v-preloader>
			</div>
			<VueApexCharts
				v-else-if="binSeries && binSeries.length"
				type="bar"
				height="350"
				:options="binChartOptions"
				:series="binSeries"
			></VueApexCharts>
			<div
				v-else-if="binUser"
				class="flex flex-col items-center justify-center h-[300px] text-center"
			>
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.no_data') || 'Нет данных' }}
				</div>
			</div>
			<div v-else class="flex flex-col items-center justify-center h-[300px] text-center">
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.enter_bin_to_search') || 'Введите БИН для поиска' }}
				</div>
			</div>
		</div>

		<!-- Fish Analytics Chart (BIN mode specific display) -->
		<div v-if="isBinMode && binSearched">
			<div class="mb-4">
				<h3 class="font-main font-semibold text-[18px] text-main mb-2">
					{{ $t('titles.fish_analytics') || 'Аналитика по видам рыб' }}
					<span v-if="binUserInfo" class="text-[14px] font-normal text-third ml-2">
						({{ binUserInfo.iin_bin }})
					</span>
				</h3>
				<!-- Active Filters Display -->
				<div v-if="hasActiveFilters" class="mb-3">
					<div class="flex flex-wrap gap-2">
						<div
							v-for="(filter, index) in activeFilters"
							:key="index"
							class="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-sm text-blue-700 flex items-center gap-2"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
									clip-rule="evenodd"
								></path>
							</svg>
							<span>{{ filter }}</span>
						</div>
					</div>
				</div>
				<div v-if="binUser" class="flex gap-8 mb-[16px]">
					<div
						class="border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px]"
					>
						<span class="font-main font-normal text-[14px] text-normal text-third">{{
							$t('titles.total_trades') || 'Всего записей в промысловый журнал'
						}}</span>
						<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
							totalFishTrades
						}}</span>
					</div>
					<div
						class="border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px]"
					>
						<span class="font-main font-normal text-[14px] text-normal text-third">{{
							$t('titles.total_fish_volume') || 'Объем рыбы (кг)'
						}}</span>
						<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
							totalFishVolume.toLocaleString() + ' кг'
						}}</span>
					</div>
				</div>
			</div>
			<div v-if="loadingFish" class="flex items-center justify-center h-[300px]">
				<v-preloader :message="{ status: null, text: null }"></v-preloader>
			</div>
			<VueApexCharts
				v-else-if="fishSeries && fishSeries.length"
				type="bar"
				height="350"
				:options="fishChartOptions"
				:series="fishSeries"
			></VueApexCharts>
			<div
				v-else-if="binUser"
				class="flex flex-col items-center justify-center h-[300px] text-center"
			>
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.no_data') || 'Нет данных' }}
				</div>
			</div>
			<div v-else class="flex flex-col items-center justify-center h-[300px] text-center">
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.enter_bin_to_search') || 'Введите БИН для поиска' }}
				</div>
			</div>
		</div>

		<!-- Users Analytics Chart -->
		<div class="mb-8" v-if="!isBinMode || !binSearched">
			<div class="mb-4">
				<h3 class="font-main font-semibold text-[18px] text-main mb-2">
					{{ $t('titles.users_analytics') || 'Статистика регистрации субъектов' }}
				</h3>
				<!-- Active Filters Display with clear button available via parent -->
				<div v-if="hasActiveFilters" class="mb-3">
					<div class="flex flex-wrap gap-2">
						<div
							v-for="(filter, index) in activeFilters"
							:key="index"
							class="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-sm text-blue-700 flex items-center gap-2 group hover:bg-blue-100 transition-colors"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
									clip-rule="evenodd"
								></path>
							</svg>
							<span>{{ filter }}</span>
							<button
								v-if="onClearFilter && getFilterType"
								@click="onClearFilter(getFilterType(filter))"
								class="ml-1 text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
								:title="$t('filters.remove_filter') || 'Удалить фильтр'"
							>
								<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
									<path
										fill-rule="evenodd"
										d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
										clip-rule="evenodd"
									></path>
								</svg>
							</button>
						</div>
					</div>
				</div>
				<div
					class="w-full border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px] mb-[16px]"
				>
					<span class="font-main font-normal text-[14px] text-normal text-third">{{
						$t('headers.total')
					}}</span>
					<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
						totalUsers
					}}</span>
				</div>
			</div>
			<div v-if="loadingUsers" class="flex items-center justify-center h-[300px]">
				<v-preloader :message="{ status: null, text: null }"></v-preloader>
			</div>
			<VueApexCharts
				v-else-if="usersSeries && usersSeries.length"
				type="bar"
				height="350"
				:options="usersChartOptions"
				:series="usersSeries"
			></VueApexCharts>
			<div v-else class="flex flex-col items-center justify-center h-[300px] text-center">
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.no_data') || 'Нет данных' }}
				</div>
			</div>
		</div>

		<!-- Quotas Analytics Chart -->
		<div class="mb-8" v-if="!isBinMode || !binSearched">
			<div class="mb-4">
				<h3 class="font-main font-semibold text-[18px] text-main mb-2">
					{{ $t('titles.quotas_analytics') || 'Статистика объемов выловленной рыбы' }}
				</h3>
				<!-- Active Filters Display -->
				<div v-if="hasActiveFilters" class="mb-3">
					<div class="flex flex-wrap gap-2">
						<div
							v-for="(filter, index) in activeFilters"
							:key="index"
							class="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-sm text-blue-700 flex items-center gap-2"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
									clip-rule="evenodd"
								></path>
							</svg>
							<span>{{ filter }}</span>
						</div>
					</div>
				</div>
				<div class="flex gap-8 mb-[16px]">
					<div
						class="border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px]"
					>
						<span class="font-main font-normal text-[14px] text-normal text-third">{{
							$t('titles.total_trades') || 'Всего записей в промысловый журнал'
						}}</span>
						<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
							totalQuotas
						}}</span>
					</div>
					<div
						class="border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px]"
					>
						<span class="font-main font-normal text-[14px] text-normal text-third">{{
							$t('titles.total_fish_volume') || 'Объем рыбы (кг)'
						}}</span>
						<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
							totalQuotasFishVolume.toLocaleString() + ' кг'
						}}</span>
					</div>
				</div>
			</div>
			<div v-if="loadingQuotas" class="flex items-center justify-center h-[300px]">
				<v-preloader :message="{ status: null, text: null }"></v-preloader>
			</div>
			<VueApexCharts
				v-else-if="quotasSeries && quotasSeries.length"
				type="bar"
				height="350"
				:options="quotasChartOptions"
				:series="quotasSeries"
			></VueApexCharts>
			<div v-else class="flex flex-col items-center justify-center h-[300px] text-center">
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.no_data') || 'Нет данных' }}
				</div>
			</div>
		</div>

		<!-- Fish Analytics Chart (General) -->
		<div class="mb-8" v-if="!isBinMode || !binSearched">
			<div class="mb-4">
				<h3 class="font-main font-semibold text-[18px] text-main mb-2">
					{{ $t('titles.fish_analytics') || 'Аналитика по видам рыб' }}
				</h3>
				<!-- Active Filters Display -->
				<div v-if="hasActiveFilters" class="mb-3">
					<div class="flex flex-wrap gap-2">
						<div
							v-for="(filter, index) in activeFilters"
							:key="index"
							class="bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-sm text-blue-700 flex items-center gap-2"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
									clip-rule="evenodd"
								></path>
							</svg>
							<span>{{ filter }}</span>
						</div>
					</div>
				</div>
				<div class="flex gap-8 mb-[16px]">
					<div
						class="border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px]"
					>
						<span class="font-main font-normal text-[14px] text-normal text-third">{{
							$t('titles.total_trades') || 'Всего записей в промысловый журнал'
						}}</span>
						<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
							totalFishTrades
						}}</span>
					</div>
					<div
						class="border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px]"
					>
						<span class="font-main font-normal text-[14px] text-normal text-third">{{
							$t('titles.total_fish_volume') || 'Объем рыбы (кг)'
						}}</span>
						<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
							totalFishVolume.toLocaleString() + ' кг'
						}}</span>
					</div>
				</div>
			</div>
			<div v-if="loadingFish" class="flex items-center justify-center h-[300px]">
				<v-preloader :message="{ status: null, text: null }"></v-preloader>
			</div>
			<VueApexCharts
				v-else-if="fishSeries && fishSeries.length"
				type="bar"
				height="350"
				:options="fishChartOptions"
				:series="fishSeries"
			></VueApexCharts>
			<div v-else class="flex flex-col items-center justify-center h-[300px] text-center">
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.no_data') || 'Нет данных' }}
				</div>
			</div>
		</div>

		<!-- Reports Analytics Chart -->
		<div v-if="!isBinMode || !binSearched">
			<div class="mb-4">
				<h3 class="font-main font-semibold text-[18px] text-main mb-2">
					{{ $t('titles.reports_analytics') || 'Аналитика 1рх отчётов' }}
					<span v-if="reportsYear" class="text-[14px] font-normal text-third ml-2">
						({{ reportsYear }})
					</span>
				</h3>
				<!-- Reports Filter -->
				<div class="table__block--filter mb-4">
					<div class="w-full row gap-y-2">
						<div class="col-12 col-md-6 col-lg-3">
							<label class="search__label">
								<span class="mr-2">{{ $t('filters.year') || 'Год' }}</span>
								<input
									v-model.number="reportsFilters.year"
									type="number"
									name="filter_year"
									class="input-linck filter-input"
									min="2020"
									:max="new Date().getFullYear()"
								/>
							</label>
						</div>
						<div class="col flex items-end gap-2">
							<button class="input-btn form__line--btn" @click="onSearchReports">
								{{ $t('button.find') }}
							</button>
							<button class="btn--link btn--link--plus" @click="onClearReportsFilters">
								{{ $t('button.clear') }}
							</button>
						</div>
					</div>
				</div>
				<div
					class="w-full border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[20px] mb-[16px]"
				>
					<span class="font-main font-normal text-[14px] text-normal text-third">{{
						$t('titles.total_reports') || 'Всего отчетов'
					}}</span>
					<span class="font-main font-bold text-[20px] text-main leading-[1.5]">{{
						reportsSeries && reportsSeries[0] && Array.isArray(reportsSeries[0].data)
							? reportsSeries[0].data.reduce((s, v) => s + Number(v || 0), 0)
							: 0
					}}</span>
				</div>
			</div>
			<div v-if="loadingReports" class="flex items-center justify-center h-[300px]">
				<v-preloader :message="{ status: null, text: null }"></v-preloader>
			</div>
			<VueApexCharts
				v-else-if="reportsSeries && reportsSeries.length"
				type="bar"
				height="350"
				:options="reportsChartOptions"
				:series="reportsSeries"
			></VueApexCharts>
			<div v-else class="flex flex-col items-center justify-center h-[300px] text-center">
				<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.no_data') || 'Нет данных' }}
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped></style>
